import { useMemo } from 'react';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';

type UseWebsiteV2FeatureReturnType = Readonly<{
  value: boolean;
  isLoading: boolean;
}>;

/**
 * Per-event `useWebsiteV2` flag: the carbon website editor fully replaces the admin website
 * experience (editor route, absorbed page-editor routes, nav/dashboard website surface) for the
 * event. No remote-flag component on purpose — the version is entirely per-event. It is set at
 * create for `createWeddingWebsiteV2` rollout events, flipped from Settings → Website by an
 * enrolled event's hosts or a super-admin, and toggleable in the Event Service Center. See
 * doc/website-v2/PLAN.md and doc/website-v2/rollout-handoff.md.
 *
 * `isLoading` is true until the flag source resolves — callers must wait before acting on
 * `value`, otherwise routing decisions can fire against the loading-default `false`.
 */
export const useWebsiteV2Feature = (): UseWebsiteV2FeatureReturnType => {
  const [isEnabled, isFlagLoading] = useIsFeatureEnabled({ eventFeatureFlagKey: 'useWebsiteV2' });

  return useMemo(
    () => ({
      value: isEnabled,
      isLoading: isFlagLoading,
    }),
    [isEnabled, isFlagLoading],
  );
};
